/**
 * "Added when" presets for the catalog filter bar.
 *
 * Pure and dependency-free so the boundaries are unit-tested — `vitest.config.ts`
 * only matches `src/**\/*.test.ts`, so this cannot live inside the component.
 *
 * Boundaries are LOCAL, not UTC. "Today" means the operator's today; computing
 * it in UTC would move the cut-off by hours and, west of Greenwich, would hide
 * rows added this morning. The values returned are absolute instants, so the
 * server still compares them against a UTC `createdAt` correctly.
 */

export type DateRangePreset = 'all' | 'today' | 'last-week' | 'this-month' | 'this-year' | 'custom'

export const DATE_RANGE_PRESETS: { value: DateRangePreset; label: string }[] = [
  { value: 'all', label: 'Any time' },
  { value: 'today', label: 'Today' },
  { value: 'last-week', label: 'Last week' },
  { value: 'this-month', label: 'This month' },
  { value: 'this-year', label: 'This year' },
  { value: 'custom', label: 'Custom range…' },
]

/** An absolute window. Either end may be open. */
export type DateRange = { createdFrom?: string; createdTo?: string }

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

/** The last millisecond of `d`'s local day, so a range's end date is inclusive. */
function endOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999)
}

/**
 * The window a preset selects.
 *
 * None of them sets `createdTo`: nothing is created in the future, so an upper
 * bound would only be a chance to get the boundary wrong. `custom` is the
 * exception and is handled by `customRange`, since only the caller knows the
 * dates.
 *
 * "Last week" is the last SEVEN DAYS including today, not the previous calendar
 * week — a filter bar reads as "recently", and a Monday showing nothing because
 * the calendar week just rolled over would look broken.
 */
export function rangeForPreset(preset: DateRangePreset, now = new Date()): DateRange {
  switch (preset) {
    case 'today':
      return { createdFrom: startOfDay(now).toISOString() }
    case 'last-week': {
      const from = startOfDay(now)
      from.setDate(from.getDate() - 6)
      return { createdFrom: from.toISOString() }
    }
    case 'this-month':
      return { createdFrom: new Date(now.getFullYear(), now.getMonth(), 1).toISOString() }
    case 'this-year':
      return { createdFrom: new Date(now.getFullYear(), 0, 1).toISOString() }
    case 'all':
    case 'custom':
      return {}
  }
}

/**
 * A custom window from two `<input type="date">` values (`YYYY-MM-DD`).
 *
 * Both ends are optional so "everything since March" and "everything up to
 * March" both work. The end date is widened to the end of that day, so picking
 * the same date for both selects that whole day rather than an empty instant.
 *
 * Parsed field-by-field rather than with `new Date('YYYY-MM-DD')`, which the
 * spec says to read as UTC midnight — that would shift the boundary by the
 * viewer's offset and silently drop a day's rows.
 */
export function customRange(from: string, to: string): DateRange {
  const range: DateRange = {}
  const start = parseDateInput(from)
  const end = parseDateInput(to)
  if (start) range.createdFrom = startOfDay(start).toISOString()
  if (end) range.createdTo = endOfDay(end).toISOString()
  return range
}

function parseDateInput(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim())
  if (!m) return null
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * Which preset a stored range represents, so the dropdown shows the right label
 * after a reload or when the filters are cleared.
 *
 * A range that matches no preset is `custom` — that is what a hand-picked window
 * is, and guessing a nearby preset would misreport what is being filtered.
 */
export function presetForRange(range: DateRange, now = new Date()): DateRangePreset {
  if (!range.createdFrom && !range.createdTo) return 'all'
  if (!range.createdTo) {
    for (const preset of ['today', 'last-week', 'this-month', 'this-year'] as const) {
      if (rangeForPreset(preset, now).createdFrom === range.createdFrom) return preset
    }
  }
  return 'custom'
}
